/**
 * @file OpenTelemetry Fetch Reject Handler
 * @description Handles failed HTTP fetch responses by logging errors, adding OpenTelemetry events, setting span attributes, and recording metrics.
 */

import { EVENTS, SEVERITY_TEXT, STAGES } from '../../constants';
import { log } from '../../logging';
import { getDynamicHttpLogAttributes } from '../../utils/getDynamicHttpLogAttributes';
import { SEMATTRS_HTTP_STATUS_CODE } from '@opentelemetry/semantic-conventions';
import { HTTP_STATUS_TEXT } from '../../constants/attributeNames';
import { SpanStatusCode } from '@opentelemetry/api';
import { withSafelyExecute } from '../../utils/withSafelyExecute';
import { getDuration, getIdsFromSpan } from '../../utils';
import { otlHandleHttpResponseMetrics } from '../../utils/otlHandleHttpResponseMetrics';

/**
 * Handles a failed HTTP fetch response, adding OpenTelemetry events, logging errors, and recording metrics.
 *
 * @param {Object} span - The OpenTelemetry span associated with the request.
 * @param {string} url - The URL of the HTTP request.
 * @param {string} method - The HTTP method used in the request (e.g., 'GET', 'POST').
 * @param {Object} error - The error object representing the failed response.
 * @param {number} error.status - The HTTP response status code.
 * @param {string} error.statusText - The HTTP response status text.
 * @param {Object} error.responseData - The response data associated with the error.
 * @param {string} error.message - The error message.
 */
function otlFetchReject(span, url, method, error) {
    const { status, statusText, responseData } = error;
    span.addEvent(EVENTS.RESPONSE_ERROR);

    log(`Request to ${url} failed`, SEVERITY_TEXT.ERROR, {
        url,
        status,
        error: JSON.stringify(responseData),
        ...getIdsFromSpan(span),
        ...getDynamicHttpLogAttributes({ url, stage: STAGES.RESPONSE }),
    });
    span.setAttributes({ [SEMATTRS_HTTP_STATUS_CODE]: status, [HTTP_STATUS_TEXT]: statusText });
    span.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
    span.end();
    otlHandleHttpResponseMetrics({ method, url, duration: getDuration(span), status });
}

export default withSafelyExecute(otlFetchReject);
